import { jsx as _jsx } from "react/jsx-runtime";
import { getAllKeys } from '@iotv/datamodel-core';
import { IconButton, Tooltip } from '@mui/material';
import React, { useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getDefaultObjectToRowFn, useGoogleRowImport } from '.';
import { AppMainViewContext } from '../../../ContextProviders/AppContextProvider';
import { useGoogleSheetSelectors } from '../../../Google/Sheets/UseGoogleSheetSelector';
import { useDataGrid } from '../../../hooks/DataGrid';
import { useSelectedObjects } from '../../../hooks/useSelectedObjects';
import { ActionIcons } from '../../../Iconography';
import { Severity } from '../../../types/AppTypes';
import { addSelectColumn } from '../../../v2/components/generic/ObjectsDataTable/addSelectColumn';
import { useGoogleSpreadsheetDataExport } from './useGoogleSpreadsheetDataExport';
const debug = process.env.REACT_APP_DEBUG && false;
export const useSimpleIO = ({ ioType, columnIndexedKeys, plainObjects, objectToRowFunction, rowToObjectFunction, fileSelectRef, setPlainObjectsInParent, }) => {
    const [{ contextCustomer, userSelectedGroup, UserFunctions: userFunctions, TransactionFunctions: transactionFunctions, isSmallView }] = useContext(AppMainViewContext);
    const { components: { SpreadsheetSelector, SheetSelector, RangeSelector, SpeadsheetUrlLink }, queryParams: { getRequest, setRequest }, state: { error: selectSheetsError, selectedSheet, setSelectedSheet, selectedFile, setSelectedFile }, } = useGoogleSheetSelectors({ fileSelectRef });
    const containedKeys = columnIndexedKeys ?? getAllKeys(plainObjects);
    const keyConstrainedObjects = plainObjects.map((object) => {
        const { id, type } = object;
        return containedKeys.reduce((a, k) => {
            a[k] = object[k] ?? null;
            return a;
        }, { type, id });
    });
    const transformer = objectToRowFunction ?? getDefaultObjectToRowFn(containedKeys);
    const rowTransformedObjects = [containedKeys, ...keyConstrainedObjects.map(transformer).map(({ row }) => row)];
    const { components: { ExportButton, WorkingSpinner: ExportWokingSpinner }, state: { error: exportError }, handlers: { clearError: clearExportError }, } = useGoogleSpreadsheetDataExport({ spreadsheetData: rowTransformedObjects, provideExportButton: true, setRequest });
    const { components: { WorkingSpinner: ImportWorkingSpinner, ImportButton }, state: { spreadsheetData, gridColumnMap, columnHeaders, error: importError }, handlers: { clearError: clearImportError }, } = useGoogleRowImport({ contextCustomer: undefined, rowToObjectFunction, getRequest, provideImportButton: true });
    const { state: [[selectedObjects, setSelectedObjects]], handlers: { isInSelectedObjects, togglePresenceInSelectedObjects }, } = useSelectedObjects([], spreadsheetData);
    // const columns: GridColumns<CommonObjectType> = columnHeaders ? columnHeaders.sort(defaultKeySorter).map((header) => ({ field: header,  })) : []
    debug && console.log('GRID COLUMN MAP', gridColumnMap);
    const columns = gridColumnMap
        ? Object.values(gridColumnMap)
        : (columnHeaders?.map((header) => ({ field: header })) ?? []);
    const rows = spreadsheetData ?? [];
    const { columns: selectableColumns, rows: selectableRows } = addSelectColumn({
        rows,
        columns,
        isInSelectedObjects,
        selectedObjects,
        setSelectedObjects,
        togglePresenceInSelectedObjects,
    });
    const { state: dataGridState, components: { DataGrid }, } = useDataGrid({
        columns: selectableColumns,
        rows: selectableRows,
        autoHeight: true,
        getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'),
        rowHeight: 24,
        columnHeaderHeight: 24,
        initialState: {
            pagination: {
                paginationModel: {
                    pageSize: 10,
                },
            },
        },
    });
    const displayMessage = (content, type, severity = Severity.error) => {
        userFunctions.setUserMessage({ id: uuidv4(), content, label: content, type, severity });
    };
    if (exportError) {
        clearExportError();
        displayMessage(exportError.message, 'MessageBar');
    }
    if (importError) {
        clearImportError();
        displayMessage(importError.message, 'MessageBar');
    }
    const LoadSelectedObjects = selectedObjects.length > 0 ? (_jsx(Tooltip, { title: "Import selected items", placement: "top", arrow: true, children: _jsx(IconButton, { onClick: () => setPlainObjectsInParent(selectedObjects), children: _jsx(ActionIcons.Import, {}) }) })) : null;
    const x = {
        state: {
            selectedFile,
            setSelectedFile,
            selectedSheet,
            setSelectedSheet,
            dataGridProps: dataGridState.dataGridProps,
        },
        components: {
            SpreadsheetSelector,
            SheetSelector,
            RangeSelector,
            SpeadsheetUrlLink,
            ImportButton,
            ImportWorkingSpinner,
            ExportButton,
            ExportWokingSpinner,
            LoadSelectedObjects,
            DataGrid,
        },
    };
    return x;
};
